import apiClient from '../../../common/functions/api_axios_instance.function';
import { handleApiError } from '../../../common/functions/errorHandler.function';
import {
  CREATE_INVOICE_COMMENT_ADMIN,
  GET_INVOICE_COMMENTS_ADMIN,
  UPDATE_INVOICE_COMMENT_ADMIN,
  DELETE_INVOICE_COMMENT_ADMIN,
  CREATE_INVOICE_COMMENT_APPROVAL,
  GET_INVOICE_COMMENTS_APPROVAL,
  UPDATE_INVOICE_COMMENT_APPROVAL,
  DELETE_INVOICE_COMMENT_APPROVAL,
} from '../../../common/constants/constants';

/**
 * Create a new comment for an invoice
 * @param {Object} commentData - Comment data
 * @param {number} commentData.invoice_id - Invoice ID
 * @param {string} commentData.comment - Comment message
 * @param {number} commentData.comment_by_roleId - Role ID of the user creating the comment
 * @param {string} commentData.comment_by_roleName - Role name of the user creating the comment
 * @param {string} userType - 'admin' or 'approval' to determine which endpoint to use
 * @returns {Promise<Object>} API response
 */
export const createInvoiceComment = async (commentData, userType = 'admin') => {
  try {
    const endpoint =
      userType === 'approval' ? CREATE_INVOICE_COMMENT_APPROVAL : CREATE_INVOICE_COMMENT_ADMIN;
    const response = await apiClient.post(endpoint, commentData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get all comments for an invoice
 * @param {number} invoiceId - Invoice ID
 * @param {string} userType - 'admin' or 'approval' to determine which endpoint to use
 * @returns {Promise<Array>} Array of comments
 */
export const getInvoiceComments = async (invoiceId, userType = 'admin') => {
  try {
    const endpoint =
      userType === 'approval'
        ? GET_INVOICE_COMMENTS_APPROVAL(invoiceId)
        : GET_INVOICE_COMMENTS_ADMIN(invoiceId);
    const response = await apiClient.get(endpoint);
    // Handle API response structure: { status, message, response: [...] }
    return response?.data?.response || [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update an existing comment
 * @param {number} commentId - Comment ID
 * @param {Object} updateData - Updated comment data
 * @param {string} updateData.comment - Updated comment message
 * @param {string} userType - 'admin' or 'approval' to determine which endpoint to use
 * @returns {Promise<Object>} API response
 */
export const updateInvoiceComment = async (commentId, updateData, userType = 'admin') => {
  try {
    const endpoint =
      userType === 'approval'
        ? UPDATE_INVOICE_COMMENT_APPROVAL(commentId)
        : UPDATE_INVOICE_COMMENT_ADMIN(commentId);
    const response = await apiClient.put(endpoint, updateData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Delete a comment
 * @param {number} commentId - Comment ID
 * @param {string} userType - 'admin' or 'approval' to determine which endpoint to use
 * @returns {Promise<Object>} API response
 */
export const deleteInvoiceComment = async (commentId, userType = 'admin') => {
  try {
    const endpoint =
      userType === 'approval'
        ? DELETE_INVOICE_COMMENT_APPROVAL(commentId)
        : DELETE_INVOICE_COMMENT_ADMIN(commentId);
    const response = await apiClient.delete(endpoint);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
